import type { SaleOrder, SaleOrderLine, SaleOrderWithLines } from '@/types/odoo';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { PartnerWarningBanner } from '@/components/ui/PartnerWarningBanner';

/**
 * Shared offerte / verkooporder renderer. Drives BOTH the standalone `OfferteTab`
 * (ProjectView + WarehouseWerfDetailView) and the Dashboard's `DashboardOfferteSection`.
 * Read-only; all data arrives as props from the project-detail payload — the server
 * already applies warehouse amount-stripping (offerte-visibility.ts) upstream.
 *
 * `embedded` drops the big "Offerte / Verkooporder" heading + client line for use
 * inside a DashboardSection shell (which supplies its own title chrome).
 */

const SO_STATE_LABELS: Record<string, string> = {
  sale: 'Verkooporder',
  draft: 'Offerte',
  sent: 'Offerte verzonden',
  cancel: 'Geannuleerd',
  done: 'Vergrendeld',
};

function formatDate(dateStr: string | false): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('nl-BE', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

interface SaleOrderCardProps {
  saleOrder: SaleOrder;
  lines: SaleOrderLine[];
}

export function SaleOrderCard({ saleOrder, lines }: SaleOrderCardProps) {
  const stateLabel = SO_STATE_LABELS[saleOrder.state] ?? saleOrder.state;
  const clientName = saleOrder.partner_id ? saleOrder.partner_id[1] : '-';

  return (
    <div className="mb-6">
      {/* Info card */}
      <div className="bg-gray-50 rounded-xl p-5 mb-4">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Verkooporder</dt>
            <dd className="text-sm font-bold text-td-blue mt-0.5">{saleOrder.name}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Klant</dt>
            <dd className="text-sm font-bold text-td-navy mt-0.5">{clientName}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Status</dt>
            <dd className="mt-0.5">
              <StatusBadge stage={stateLabel} />
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Besteldatum</dt>
            <dd className="text-sm font-bold text-td-navy mt-0.5">{formatDate(saleOrder.date_order)}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Klantreferentie</dt>
            <dd className="text-sm font-bold text-td-navy mt-0.5">{saleOrder.client_order_ref || '-'}</dd>
          </div>
          {saleOrder.amount_total != null && (
            <div>
              <dt className="text-[10px] font-bold text-td-muted uppercase tracking-wide">Totaal</dt>
              <dd className="text-sm font-bold text-td-navy mt-0.5">{formatCurrency(saleOrder.amount_total)}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Artikellijst */}
      {lines.length > 0 && (
        <div>
          <h3 className="text-sm font-extrabold text-td-navy mb-3">Artikellijst</h3>
          <div className="rounded-xl overflow-hidden border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th scope="col" className="text-left px-4 py-2.5 text-[10px] font-bold text-td-muted uppercase tracking-wide">
                    Artikel
                  </th>
                  <th scope="col" className="text-right px-4 py-2.5 text-[10px] font-bold text-td-muted uppercase tracking-wide">
                    Aantal
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  // Section header row (e.g. "1.0 Materialen", "1.1 Systeem")
                  if (line.display_type === 'line_section') {
                    return (
                      <tr key={line.id} className="bg-td-navy/5">
                        <td
                          colSpan={2}
                          className="px-4 py-2.5 text-sm font-extrabold text-td-navy"
                        >
                          {line.name}
                        </td>
                      </tr>
                    );
                  }

                  // Note row
                  if (line.display_type === 'line_note') {
                    return (
                      <tr key={line.id}>
                        <td
                          colSpan={2}
                          className="px-4 py-1.5 text-xs text-td-muted italic"
                        >
                          {line.name}
                        </td>
                      </tr>
                    );
                  }

                  // Regular product line
                  return (
                    <tr key={line.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                      <td className="px-4 py-2 pl-6 text-td-navy">{line.name}</td>
                      <td className="px-4 py-2 text-td-navy text-right whitespace-nowrap">
                        {line.product_uom_qty} {line.product_uom_id ? line.product_uom_id[1] : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {lines.length === 0 && (
        <div className="text-center py-8">
          <p className="text-td-muted text-sm">Geen artikelen gevonden</p>
        </div>
      )}
    </div>
  );
}

interface OfferteContentProps {
  saleOrders: SaleOrderWithLines[];
  saleOrder: SaleOrder | null;       // backward compat fallback
  lines: SaleOrderLine[];            // backward compat fallback
  partnerWarning?: string | null;
  /** Drop the standalone heading + client line for use inside a DashboardSection. */
  embedded?: boolean;
}

export function OfferteContent({ saleOrders, saleOrder, lines, partnerWarning, embedded }: OfferteContentProps) {
  // Build the effective list: prefer saleOrders[], fall back to legacy single SO
  let effectiveOrders: SaleOrderWithLines[] = saleOrders;
  if (!effectiveOrders || effectiveOrders.length === 0) {
    if (saleOrder) {
      // Legacy single-SO fallback: it IS the base contract.
      effectiveOrders = [{ ...saleOrder, lines, role: 'basis' }];
    } else {
      effectiveOrders = [];
    }
  }

  if (effectiveOrders.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p className="text-td-muted text-sm">Geen verkooporder gekoppeld</p>
      </div>
    );
  }

  const isPlural = effectiveOrders.length > 1;
  const firstOrder = effectiveOrders[0];
  const clientName = firstOrder.partner_id ? firstOrder.partner_id[1] : '-';

  return (
    <div>
      {partnerWarning && <PartnerWarningBanner message={partnerWarning} />}

      {/* Header — suppressed when embedded in a DashboardSection (which supplies its own title). */}
      {!embedded && (
        <div className="mb-5">
          <h2 className="text-lg font-extrabold text-td-navy">
            {isPlural ? 'Offertes / Verkooporders' : 'Offerte / Verkooporder'}
          </h2>
          <p className="text-sm text-td-muted mt-0.5">
            {clientName !== '-' && <span className="text-td-navy font-semibold">{clientName}</span>}
          </p>
        </div>
      )}

      {/* Render each SO as a card */}
      {effectiveOrders.map((so) => (
        <SaleOrderCard
          key={so.id}
          saleOrder={so}
          lines={so.lines}
        />
      ))}
    </div>
  );
}
